import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Box, Button, Group, Loader, Text, ColorSwatch, Tooltip } from '@mantine/core';
import { IconArrowBackUp, IconArrowForwardUp, IconTrash, IconDeviceFloppy, IconRestore } from '@tabler/icons-react';
import shapeit from '../lib/shapeit';

// ── shape snapping (rough stroke → clean circle / arrow / polygon) ─────────────
type Pt = [number, number];
type Shape =
  | { kind: 'pen'; pts: Pt[] }
  | { kind: 'poly'; pts: Pt[]; closed: boolean }
  | { kind: 'circle'; center: Pt; radius: number }
  | { kind: 'arrow'; a: Pt; b: Pt };
type Drawable = Shape & { color: string };

const closeWith = (s: Pt[], over: number): Pt[] => {
  const a = s[0], b = s[Math.min(6, s.length - 1)];
  const dx = b[0] - a[0], dy = b[1] - a[1], dl = Math.hypot(dx, dy) || 1;
  const out = s.slice(); out.push([a[0], a[1]]);
  for (let t = 1; t <= Math.round(over / 5); t++) { const d = t * 5; out.push([a[0] + (dx / dl) * d, a[1] + (dy / dl) * d]); }
  return out;
};
function recognizeRobust(s: Pt[]): any {
  const variants = [s, ...[22, 30, 38, 48, 60, 75].map((o) => closeWith(s, o))];
  let fallback: any = null;
  for (const v of variants) {
    let r: any; try { r = shapeit(v as Pt[]); } catch { continue; }
    if (!fallback) fallback = r;
    if (r && r.name !== 'open polygon') return r;
  }
  return fallback || { name: 'open polygon' };
}
function fitCircle(pts: Pt[]): { center: Pt; radius: number } | null {
  let n = pts.length, Sx = 0, Sy = 0, Sxx = 0, Syy = 0, Sxy = 0, Sxz = 0, Syz = 0, Sz = 0;
  for (const [x, y] of pts) { const z = x * x + y * y; Sx += x; Sy += y; Sxx += x * x; Syy += y * y; Sxy += x * y; Sxz += x * z; Syz += y * z; Sz += z; }
  const M = [[Sxx, Sxy, Sx], [Sxy, Syy, Sy], [Sx, Sy, n]], b = [-Sxz, -Syz, -Sz];
  const det = (m: number[][]) => m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) - m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) + m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
  const D0 = det(M); if (!D0) return null;
  const repl = (c: number) => { const x = M.map((r) => r.slice()); for (let i = 0; i < 3; i++) x[i][c] = b[i]; return x; };
  const D = det(repl(0)) / D0, E = det(repl(1)) / D0, F = det(repl(2)) / D0;
  return { center: [-D / 2, -E / 2], radius: Math.sqrt(Math.max(0, (D * D + E * E) / 4 - F)) };
}
function strokeMetrics(pts: Pt[]) {
  const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
  const minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
  const w = Math.max(1, maxx - minx), h = Math.max(1, maxy - miny);
  // shoelace area of the (closed) stroke ÷ bounding-box area
  let a = 0; for (let i = 0; i < pts.length; i++) { const [x1, y1] = pts[i], [x2, y2] = pts[(i + 1) % pts.length]; a += x1 * y2 - x2 * y1; }
  return { minx, maxx, miny, maxy, w, h, diag: Math.hypot(w, h), fill: (Math.abs(a) / 2) / (w * h), aspect: Math.max(w, h) / Math.min(w, h) };
}
const fitRect = (m: ReturnType<typeof strokeMetrics>): Shape =>
  ({ kind: 'poly', closed: true, pts: [[m.minx, m.miny], [m.maxx, m.miny], [m.maxx, m.maxy], [m.minx, m.maxy]] });
const RECT_NAMES = new Set(['square', 'rectangle', 'rhombus', 'quadrilateral', 'concave quadrilateral', 'trapezoid']);

function snap(pts: Pt[]): Shape {
  const out = recognizeRobust(pts);
  const name: string = Array.isArray(out) ? (out as any).name : (out && (out as any).name);
  // straight stroke → arrow
  if (Array.isArray(out) && name === 'vector') return { kind: 'arrow', a: out[0] as Pt, b: out[out.length - 1] as Pt };
  // aggressive rectangle: shapeit calls it a quad, OR it fills its bounding box
  // like a box (circle ≈ 0.79, rect ≈ 0.95+), OR it's elongated (≥1.5:1 — a
  // longish loop is a box, not a circle).
  const m = strokeMetrics(pts);
  if (RECT_NAMES.has(name) || m.fill >= 0.85 || m.aspect >= 1.5) return fitRect(m);
  // round → circle (shapeit's circle, else least-squares fit, guarded)
  if (out && (out as any).radius != null && Array.isArray((out as any).center)) return { kind: 'circle', center: (out as any).center as Pt, radius: (out as any).radius };
  if (Array.isArray(out) && (name === 'open polygon' || out.length > 4)) {
    const c = fitCircle(pts);
    if (c && isFinite(c.radius) && c.radius <= m.diag * 1.5) return { kind: 'circle', center: c.center, radius: c.radius };
  }
  if (Array.isArray(out)) return { kind: 'poly', pts: (out as any).map((v: any) => (Array.isArray(v) ? v : [v.x, v.y])), closed: name !== 'open polygon' };
  return { kind: 'pen', pts };
}

const STROKE = '#ef4444';
const PALETTE = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#a855f7', '#ec4899', '#ffffff', '#111827'];
function drawOne(ctx: CanvasRenderingContext2D, d: Drawable, lw: number) {
  ctx.strokeStyle = d.color; ctx.lineWidth = lw; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.setLineDash([]);
  if (d.kind === 'circle') { ctx.beginPath(); ctx.arc(d.center[0], d.center[1], d.radius, 0, 2 * Math.PI); ctx.stroke(); return; }
  if (d.kind === 'arrow') {
    ctx.beginPath(); ctx.moveTo(d.a[0], d.a[1]); ctx.lineTo(d.b[0], d.b[1]); ctx.stroke();
    const ang = Math.atan2(d.b[1] - d.a[1], d.b[0] - d.a[0]), len = Math.max(14, lw * 5), sp = Math.PI / 7;
    ctx.beginPath();
    ctx.moveTo(d.b[0], d.b[1]); ctx.lineTo(d.b[0] - len * Math.cos(ang - sp), d.b[1] - len * Math.sin(ang - sp));
    ctx.moveTo(d.b[0], d.b[1]); ctx.lineTo(d.b[0] - len * Math.cos(ang + sp), d.b[1] - len * Math.sin(ang + sp));
    ctx.stroke(); return;
  }
  const pts = d.pts; if (!pts.length) return;
  ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
  if (d.kind === 'poly' && d.closed) ctx.lineTo(pts[0][0], pts[0][1]);
  ctx.stroke();
}
const scaleDrawable = (d: Drawable, k: number): Drawable => {
  if (d.kind === 'circle') return { kind: 'circle', color: d.color, center: [d.center[0] * k, d.center[1] * k], radius: d.radius * k };
  if (d.kind === 'arrow') return { kind: 'arrow', color: d.color, a: [d.a[0] * k, d.a[1] * k], b: [d.b[0] * k, d.b[1] * k] };
  return { ...d, pts: d.pts.map((p) => [p[0] * k, p[1] * k] as Pt) };
};

// ── component ──────────────────────────────────────────────────────────────────
interface Props {
  opened: boolean;
  photoUrl: string;
  onClose: () => void;
  onSave: (file: File) => Promise<void> | void;
  // Whether this photo was already edited and a pristine original is preserved.
  // When true, the "Revert to original" button is shown.
  canRevert?: boolean;
  // Restore the pristine original (drops the edit). Awaited before closing.
  onRevert?: () => Promise<void> | void;
}
export function PhotoAnnotator({ opened, photoUrl, onClose, onSave, canRevert, onRevert }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [strokes, setStrokes] = useState<Drawable[]>([]);
  const [redo, setRedo] = useState<Drawable[]>([]);
  const drawingRef = useRef<Pt[] | null>(null);
  const [color, setColor] = useState(STROKE);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);

  // load original via fetch→blob URL so the canvas isn't CORS-tainted on export
  useEffect(() => {
    if (!opened || !photoUrl) return;
    let revoked: string | null = null, cancelled = false;
    setLoading(true); setError(null); setStrokes([]); setRedo([]); imgRef.current = null; setDims(null);
    (async () => {
      try {
        let src = photoUrl;
        try { const r = await fetch(photoUrl, { mode: 'cors' }); if (r.ok) { const b = await r.blob(); src = revoked = URL.createObjectURL(b); } } catch { /* fall back to direct src */ }
        const img = new Image();
        if (src === photoUrl) img.crossOrigin = 'anonymous';
        img.onload = () => {
          if (cancelled) return;
          // big & centered: fit as large as possible within the viewport
          const maxW = Math.min(1100, Math.max(320, window.innerWidth - 96));
          const maxH = Math.max(280, window.innerHeight * 0.78);
          const ratio = (img.naturalHeight || 1) / (img.naturalWidth || 1);
          let w = maxW, h = w * ratio;
          if (h > maxH) { h = maxH; w = h / ratio; }
          imgRef.current = img; setDims({ w: Math.round(w), h: Math.round(h) }); setLoading(false);
        };
        img.onerror = () => { if (!cancelled) { setError('Could not load image (CORS).'); setLoading(false); } };
        img.src = src;
      } catch { if (!cancelled) { setError('Could not load image.'); setLoading(false); } }
    })();
    return () => { cancelled = true; if (revoked) URL.revokeObjectURL(revoked); };
  }, [opened, photoUrl]);

  const redraw = useCallback(() => {
    const cv = canvasRef.current, img = imgRef.current; if (!cv || !img || !dims) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    const lw = Math.max(3, dims.w / 220);
    ctx.clearRect(0, 0, dims.w, dims.h);
    ctx.drawImage(img, 0, 0, dims.w, dims.h);
    for (const d of strokes) drawOne(ctx, d, lw);
    if (drawingRef.current && drawingRef.current.length) drawOne(ctx, { kind: 'pen', pts: drawingRef.current, color }, lw);
  }, [dims, strokes, color]);

  useEffect(() => { redraw(); }, [redraw]);

  const pos = (e: React.PointerEvent): Pt => {
    const r = canvasRef.current!.getBoundingClientRect();
    return [((e.clientX - r.left) / r.width) * dims!.w, ((e.clientY - r.top) / r.height) * dims!.h];
  };
  const onDown = (e: React.PointerEvent) => { if (!dims) return; (e.target as Element).setPointerCapture(e.pointerId); drawingRef.current = [pos(e)]; redraw(); };
  const onMove = (e: React.PointerEvent) => { if (!drawingRef.current) return; drawingRef.current.push(pos(e)); redraw(); };
  const onUp = () => {
    const pts = drawingRef.current; drawingRef.current = null;
    if (!pts || pts.length < 3) { redraw(); return; }
    setStrokes((s) => [...s, { ...snap(pts), color }]); setRedo([]);
  };

  const undo = () => setStrokes((s) => { if (!s.length) return s; setRedo((r) => [...r, s[s.length - 1]]); return s.slice(0, -1); });
  const doRedo = () => setRedo((r) => { if (!r.length) return r; setStrokes((s) => [...s, r[r.length - 1]]); return r.slice(0, -1); });

  const save = async () => {
    const img = imgRef.current; if (!img || !dims) return;
    setSaving(true);
    try {
      const k = (img.naturalWidth || dims.w) / dims.w; // display → natural
      const off = document.createElement('canvas');
      off.width = img.naturalWidth || dims.w; off.height = img.naturalHeight || dims.h;
      const ctx = off.getContext('2d')!;
      ctx.drawImage(img, 0, 0, off.width, off.height);
      const lw = Math.max(3, dims.w / 220) * k;
      for (const d of strokes) drawOne(ctx, scaleDrawable(d, k), lw);
      const blob: Blob | null = await new Promise((res) => off.toBlob((b) => res(b), 'image/jpeg', 0.92));
      if (!blob) throw new Error('export failed (image is cross-origin / tainted)');
      const file = new File([blob], `annotated-${Date.now()}.jpg`, { type: 'image/jpeg' });
      await onSave(file);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not save annotation.');
    } finally { setSaving(false); }
  };

  // Drop the current edit and restore the pristine original. Only available
  // when the photo was already edited (canRevert) — the original is preserved
  // under photo.original_url by the save path.
  const revert = async () => {
    if (!onRevert) return;
    setReverting(true);
    try {
      await onRevert();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not revert to original.');
    } finally { setReverting(false); }
  };

  return (
    <Modal opened={opened} onClose={onClose} size="auto" centered withCloseButton={false} padding="md"
      styles={{ content: { background: '#0c0e12' }, body: { padding: 12 } }}>
      <Group justify="space-between" mb="sm" wrap="nowrap">
        <Group gap="sm">
          <Button variant="default" size="md" leftSection={<IconArrowBackUp size={20} />} onClick={undo} disabled={!strokes.length}>Undo</Button>
          <Button variant="default" size="md" leftSection={<IconArrowForwardUp size={20} />} onClick={doRedo} disabled={!redo.length}>Redo</Button>
          <Button variant="subtle" color="red" size="md" leftSection={<IconTrash size={18} />} onClick={() => { setStrokes([]); setRedo([]); }} disabled={!strokes.length}>Clear</Button>
        </Group>
        <Group gap={8} wrap="nowrap" px="xs">
          {PALETTE.map((c) => (
            <Tooltip key={c} label={c} withArrow openDelay={400}>
              <ColorSwatch
                component="button"
                color={c}
                onClick={() => setColor(c)}
                size={26}
                style={{
                  cursor: 'pointer',
                  border: color === c ? '2px solid #fff' : '2px solid transparent',
                  boxShadow: color === c ? '0 0 0 2px #3b82f6' : 'none',
                }}
              />
            </Tooltip>
          ))}
        </Group>
        <Group gap="sm">
          {canRevert && (
            <Button variant="default" color="yellow" size="md" leftSection={<IconRestore size={18} />} loading={reverting} onClick={revert} disabled={saving}>Revert to original</Button>
          )}
          <Button variant="default" size="md" onClick={onClose} disabled={saving || reverting}>Cancel</Button>
          <Button size="md" leftSection={<IconDeviceFloppy size={18} />} loading={saving} onClick={save} disabled={loading || !!error || !strokes.length || reverting}>Save as new photo</Button>
        </Group>
      </Group>

      <Box style={{ position: 'relative', width: dims?.w ?? 640, height: dims?.h ?? 420, background: '#0c0e12', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
        {loading && <Loader />}
        {error && <Text size="sm" c="red" px="md" ta="center">{error}</Text>}
        {!loading && !error && (
          <canvas
            ref={canvasRef}
            width={dims?.w} height={dims?.h}
            style={{ width: dims?.w, height: dims?.h, borderRadius: 8, touchAction: 'none', cursor: 'crosshair', display: 'block' }}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}
          />
        )}
      </Box>
    </Modal>
  );
}
